import * as React from 'react'
import { Link } from 'react-router-dom'
import type { HoneybookCandidateDto, HoneybookReviewItemDto } from '@lib/dto/honeybook.dto'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChoiceList, ChoiceRow } from '@/components/ui/choice-card'
import { ErrorState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useHoneybookReview, useResolveHoneybookReview } from '@/hooks/useHoneyBook'
import { formatEventDate } from '@/lib/eventTime'

const pct = (p: number | null): string => (p === null ? '' : `${String(Math.round(p * 100))}%`)

function guests(n: number | null): string {
  if (n === null || n === 0) return 'guests not set'
  return n === 1 ? '1 guest' : `${String(n)} guests`
}

function reasonText(item: HoneybookReviewItemDto): string {
  if (item.reason === 'email_ambiguous') return 'This event’s clients are on more than one booking in HoneyBook.'
  if (item.reason === 'clef_unsure') {
    // No probability: Clef couldn't be asked, or its pick isn't among the bookings shown.
    if (item.suggestedProbability === null) return 'No email in common, but these bookings share a name or the day with this event.'
    return `No email in common, and Clef wasn’t sure which booking this is (${pct(item.suggestedProbability)}).`
  }
  const [only] = item.candidates
  if (item.candidates.length === 1 && only !== undefined && only.date !== null) return `HoneyBook has another date for this couple: ${formatEventDate(only.date)}.`
  return 'HoneyBook has another date for this couple.'
}

function CandidateTitle({ candidate, suggested, probability }: { candidate: HoneybookCandidateDto; suggested: boolean; probability: number | null }) {
  return (
    <span className="flex items-center gap-2">
      <span className="truncate">{candidate.name ?? 'A booking with no name'}</span>
      {suggested ? <Badge tone="info">Clef’s pick · {pct(probability)}</Badge> : null}
    </span>
  )
}

function CandidateLine({ candidate }: { candidate: HoneybookCandidateDto }) {
  return (
    <span className="block truncate">
      {formatEventDate(candidate.date)} · {guests(candidate.guestCount)}
      {candidate.openUrl === null ? null : (
        <>
          {' · '}
          <a href={candidate.openUrl} target="_blank" rel="noreferrer" className="font-medium text-ink underline underline-offset-2">
            Open in HoneyBook
          </a>
        </>
      )}
    </span>
  )
}

function ReviewCard({ venueId, item }: { venueId: string; item: HoneybookReviewItemDto }) {
  const resolve = useResolveHoneybookReview(venueId)
  const [selected, setSelected] = React.useState<number | null>(item.suggestedIndex ?? (item.candidates.length === 1 ? 0 : null))
  const chosen = selected === null ? undefined : item.candidates[selected]

  return (
    <Card asChild className="space-y-3 p-4">
      <section>
        <div>
          <Link to={`/events/${item.eventId}`} className="text-sm font-medium text-ink underline-offset-2 hover:underline">
            {item.eventName}
          </Link>
          <p className="m-0 text-xs text-ink-muted">
            {formatEventDate(item.eventDate)} · {guests(item.eventGuestCount)}
          </p>
        </div>
        <p className="m-0 text-sm text-ink-muted">{reasonText(item)}</p>

        {item.candidates.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">These bookings can’t be read from HoneyBook right now.</p>
        ) : (
          <ChoiceList label={`HoneyBook bookings for ${item.eventName}`}>
            {item.candidates.map((candidate, i) => (
              <ChoiceRow
                key={candidate.projectId}
                type="radio"
                name={`honeybook-review-${item.id}`}
                checked={selected === i}
                onSelect={() => setSelected(i)}
                title={<CandidateTitle candidate={candidate} suggested={item.suggestedIndex === i} probability={item.suggestedProbability} />}
                description={<CandidateLine candidate={candidate} />}
              />
            ))}
          </ChoiceList>
        )}

        {resolve.isError ? <FormError>{requestErrorMessage(resolve.error)}</FormError> : null}

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={chosen === undefined || resolve.isPending}
            onClick={() => {
              if (chosen !== undefined) resolve.mutate({ itemId: item.id, projectId: chosen.projectId })
            }}
          >
            This is it
          </Button>
          <Button size="sm" variant="ghost" disabled={resolve.isPending} onClick={() => resolve.mutate({ itemId: item.id, projectId: null })}>
            None of these
          </Button>
        </div>
      </section>
    </Card>
  )
}

/**
 * The venue's events matching couldn't hand over to a HoneyBook booking on its
 * own (docs/honeybook.plan.md, 5.4): HoneyBook has another date for the couple,
 * their emails are on several bookings, or Clef wasn't sure. "This is it" hands
 * the event over; "None of these" keeps it the venue's own. Nothing when none
 * are waiting.
 */
export function HoneyBookReviewList({ venueId, readAt }: { venueId: string; readAt: string | null }) {
  const review = useHoneybookReview(venueId, readAt)
  if (review.isPending) return null
  if (review.isError) return <ErrorState message={requestErrorMessage(review.error)} onRetry={() => void review.refetch()} />
  const { items, total } = review.data
  if (items.length === 0) return null
  return (
    <section className="space-y-3">
      <div>
        <h3 className="m-0 text-sm font-semibold text-ink">Events to match</h3>
        <p className="m-0 text-sm text-ink-muted">
          These may already be bookings in HoneyBook. Once matched, an event follows its booking and changes only there.
        </p>
      </div>
      {total > items.length ? (
        <p className="m-0 text-sm text-ink-muted">
          Showing the oldest {items.length} of {total}. Settle these and the next ones appear.
        </p>
      ) : null}
      {items.map((item) => (
        <ReviewCard key={item.id} venueId={venueId} item={item} />
      ))}
    </section>
  )
}
